import React, { useState } from 'react';
import Swal from 'sweetalert2';
import classroomExamService from '../../services/classroomExamService';
import types from '../../services/types';
import utils from '../../utils/generalFunctions';
import HtmlEditor from '../HtmlEditor';

const NEEDS_OPTIONS = [types.classroomQuestionTypes.VERDADERO_FALSO, types.classroomQuestionTypes.OPCION_MULTIPLE, types.classroomQuestionTypes.OPCION_UNICA];

// Formulario de una pregunta del banco (5 tipos). Para Verdadero/Falso se
// puede usar el atajo "correctAnswer" en vez de escribir las 2 opciones a
// mano (el backend las genera).
const ClassroomQuestionForm = ({ classroomId, question, onClose }) => {
    const [questionTypeId, setQuestionTypeId] = useState(question?.questionTypeId || types.classroomQuestionTypes.OPCION_UNICA);
    const [statementHtml, setStatementHtml] = useState(question?.statementHtml || '');
    const [options, setOptions] = useState(question?.options?.length > 0 ? question.options : [{ optionText: '', isCorrect: false }, { optionText: '', isCorrect: false }]);
    const [correctAnswer, setCorrectAnswer] = useState(true);

    const addOption = () => setOptions([...options, { optionText: '', isCorrect: false }]);
    const removeOption = (index) => setOptions(options.filter((_, i) => i !== index));
    const updateOption = (index, field, value) => {
        const copia = [...options];
        copia[index] = { ...copia[index], [field]: value };
        setOptions(copia);
    };
    const setUnicaCorrecta = (index) => {
        setOptions(options.map((o, i) => ({ ...o, isCorrect: i === index })));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!statementHtml) {
            Swal.fire('Campo obligatorio', 'El enunciado de la pregunta es obligatorio.', 'error');
            return;
        }
        const user = utils.getUserInformation();
        const payload = {
            questionId: question?.questionId || 0,
            classroomId,
            questionTypeId,
            statementHtml,
            isActive: true,
            createdUser: user?.userId,
            modificationUser: user?.userId
        };
        if (questionTypeId === types.classroomQuestionTypes.VERDADERO_FALSO) {
            payload.correctAnswer = correctAnswer;
        } else if (NEEDS_OPTIONS.includes(questionTypeId)) {
            payload.options = options.filter((o) => o.optionText);
        }
        try {
            if (payload.questionId > 0) {
                await classroomExamService.updateQuestion(payload);
            } else {
                await classroomExamService.createQuestion(payload);
            }
            Swal.fire('Listo', 'Pregunta guardada con éxito.', 'success');
            if (onClose) onClose();
        } catch (error) {
            console.error(error);
            Swal.fire('Ha ocurrido un error', error?.response?.data?.message || 'Revisa que las opciones cumplan la regla del tipo de pregunta.', 'error');
        }
    };

    return (
        <form onSubmit={handleSubmit} className="container-fluid mt-2">
            <div className="mb-3">
                <label className="form-label">Tipo de pregunta *</label>
                <select className="form-control form-select" value={questionTypeId} onChange={(e) => setQuestionTypeId(Number(e.target.value))} disabled={!!question}>
                    <option value={types.classroomQuestionTypes.VERDADERO_FALSO}>Verdadero / Falso</option>
                    <option value={types.classroomQuestionTypes.OPCION_UNICA}>Opción única</option>
                    <option value={types.classroomQuestionTypes.OPCION_MULTIPLE}>Opción múltiple</option>
                    <option value={types.classroomQuestionTypes.SUBIR_ARCHIVO}>Subir archivo</option>
                    <option value={types.classroomQuestionTypes.PREGUNTA_ABIERTA}>Pregunta abierta</option>
                </select>
            </div>

            <div className="mb-3">
                <label className="form-label">Enunciado *</label>
                <HtmlEditor value={statementHtml} onChange={setStatementHtml} />
            </div>

            {questionTypeId === types.classroomQuestionTypes.VERDADERO_FALSO && (
                <div className="mb-3">
                    <label className="form-label">Respuesta correcta</label>
                    <select className="form-control form-select" value={correctAnswer ? '1' : '0'} onChange={(e) => setCorrectAnswer(e.target.value === '1')}>
                        <option value="1">Verdadero</option>
                        <option value="0">Falso</option>
                    </select>
                </div>
            )}

            {(questionTypeId === types.classroomQuestionTypes.OPCION_UNICA || questionTypeId === types.classroomQuestionTypes.OPCION_MULTIPLE) && (
                <div className="mb-3">
                    <label className="form-label">
                        Opciones {questionTypeId === types.classroomQuestionTypes.OPCION_UNICA ? '(marca la única correcta)' : '(marca todas las correctas)'}
                    </label>
                    {options.map((opt, index) => (
                        <div className="input-group mb-2" key={index}>
                            <div className="input-group-text">
                                <input
                                    type={questionTypeId === types.classroomQuestionTypes.OPCION_UNICA ? 'radio' : 'checkbox'}
                                    checked={opt.isCorrect}
                                    onChange={() =>
                                        questionTypeId === types.classroomQuestionTypes.OPCION_UNICA
                                            ? setUnicaCorrecta(index)
                                            : updateOption(index, 'isCorrect', !opt.isCorrect)
                                    }
                                />
                            </div>
                            <input
                                type="text"
                                className="form-control"
                                placeholder={`Opción ${index + 1}`}
                                value={opt.optionText}
                                onChange={(e) => updateOption(index, 'optionText', e.target.value)}
                            />
                            {options.length > 2 && (
                                <button type="button" className="btn btn-outline-danger" onClick={() => removeOption(index)}>
                                    <i className="fas fa-times"></i>
                                </button>
                            )}
                        </div>
                    ))}
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={addOption}>
                        <i className="fas fa-plus me-1"></i> Agregar opción
                    </button>
                </div>
            )}

            {questionTypeId === types.classroomQuestionTypes.SUBIR_ARCHIVO && (
                <div className="alert alert-secondary">El estudiante responderá subiendo un archivo. Sin opciones que configurar aquí.</div>
            )}
            {questionTypeId === types.classroomQuestionTypes.PREGUNTA_ABIERTA && (
                <div className="alert alert-secondary">El estudiante responderá con texto libre. Sin opciones que configurar aquí.</div>
            )}

            <button type="submit" className="btn btn-primary">Guardar</button>
        </form>
    );
};

export default ClassroomQuestionForm;
